import { withNamespaces } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import SoftwareAttachmentsService from "src/modules/data-protection/apis/SoftwareAttachmentsService";
import { toast } from "react-toastify";
import { connect } from 'react-redux';
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import overlayFactory from "react-bootstrap-table2-overlay";
import BootstrapTable from "react-bootstrap-table-next";
import { Button, Col, Row, Spinner, UncontrolledTooltip } from "reactstrap";
import { Accordion } from "react-bootstrap";
import { useRef, useState } from "react";
import { EMPTY_LIST, INTERNATIONAL_DATE_TIME_FORMAT } from "src/common/constants";
import AddAttachmentModal from "./components/add-attachment-modal";
import { API_BASE_URL, DocumentSources, DocumentsCategoryTypes, DocumentsConfidentialityLevels, DocumentsTypes } from "src/modules/data-protection/constants";
import debounce from "lodash.debounce";
import axios from "axios";
import DeleteAttachmentModal from "./components/delete-attachment-modal";
import DateUtils from "src/services/utils/DateUtils";
import PaperImage from "src/modules/data-protection/assets/images/paper-light.svg";
import { INFORMATION_ACCORDIONS } from "../../information/helpers/accordionDeepLink";
import useInformationAccordionFocus from "../../information/hooks/useInformationAccordionFocus";

const ATTACHMENTS_ACCORDION_KEY = INFORMATION_ACCORDIONS.ATTACHMENTS;

const Attachments = ({
    t,
    token,
    assetId
}) => {
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [showAddAttachmentModal, setShowAddAttachmentModal] = useState(false);
    const [downloadsPercents, setDownloadsPercents] = useState([]);
    const [attachmentToDelete, setAttachmentToDelete] = useState(null);

    const dateUtils = new DateUtils();

    useInformationAccordionFocus(ATTACHMENTS_ACCORDION_KEY, setActiveAccordion);

    const handleAccordionClick = () => {
        setActiveAccordion((prev) => (
            prev === ATTACHMENTS_ACCORDION_KEY ? null : ATTACHMENTS_ACCORDION_KEY
        ));
    };

    const updatePercent = useRef(
        debounce(({
            id,
            percent
        }) => {
            setDownloadsPercents((currentState) => {
                return [...currentState].map((p) => {
                    if (p.id === id) {
                        return {
                            ...p,
                            percent: percent
                        }
                    }
                    return p;
                })
            });
        }, 100)
    ).current;

    const downloadFile = ({
        id,
        originName,
        size
    }) => {
        setDownloadsPercents((currentState) => {
            return [
                ...currentState,
                {
                    id: id,
                    percent: 0
                }
            ]
        });

        axios.get(`${API_BASE_URL}/asset_document/${id}/download`, {
            responseType: "arraybuffer",
            headers: {
                Authorization: `Bearer ${token}`,
            },
            onDownloadProgress: (e) => {
                updatePercent({
                    id: id,
                    percent: Math.round((e.loaded * 100) / size)
                });
            },
        })
        .then((blob) => {
            const link = document.createElement("a");
        
            link.href = window.URL.createObjectURL(new Blob([blob.data]));
            link.setAttribute("download", originName);
        
            document.body.appendChild(link);
        
            link.click();
            link.remove();
        })
        .catch(() => {
            toast(t("An error occurred while downloading the file."), {
                type: "error"
            });
        })
        .finally(() => {
            setTimeout(() => {
                setDownloadsPercents((currentState) => {
                    return currentState.filter((p) => p.id !== id)
                });
            }, 500);
        });
    };

    const {
        data: attachmentsList = [],
        isFetching: attachmentsAreFetching,
        isFetched: attachmentsFetched,
        refetch: refetchAttachmentsList
    } = useQuery({
        queryKey: [
            "data-protection-fetch-software-attachments-list", 
            assetId
        ],
        queryFn: async () => {
            const service = SoftwareAttachmentsService.getInstance();
            return await service.list(assetId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while retrieving the attachments."), {
                type: "error"
            });
        },
    });

    const columns = [
        {
            dataField: "secondary_id",
            text: t("ID"),
            sort: false,
            style: {
                width: '80px'
            }
        },
        {
            dataField: "title",
            text: t("Title"),
            sort: true,
            style: {
                width: '220px'
            },
            formatter: (_cell, {
                title,
                origin_name,
                content_type,
                path
            }) => {
                if(content_type === 'link') {
                    return (
                        <a 
                            href={path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-break text-wrap text-truncate"
                        >
                            {title || origin_name}
                        </a>
                    )
                }

                return title || origin_name
            }
        },
        {
            dataField: "type",
            text: t("Type"),
            sort: true,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return t((DocumentsTypes.find((type) => type.value === cell))?.title)
            }
        },
        {
            dataField: "source",
            text: t("Source"),
            sort: false,
            style: {
                width: '180px',
                whiteSpace: 'normal'
            },
            formatter: (cell, row) => {
                const source = cell || row?.confluence_meta?.source;

                if (!source) {
                    return '---';
                }

                return t((DocumentSources.find((item) => item.value === source))?.title || source);
            }
        },
        {
            dataField: "category",
            text: t("Category"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cell) => {
                return t((DocumentsCategoryTypes.find((type) => type.value === cell))?.title || cell)
            }
        },
        {
            dataField: "confidentialityLevel",
            text: t("Confidentiality Level"),
            sort: true,
            style: {
                width: '120px'
            },
            formatter: (cell) => {
                return t((DocumentsConfidentialityLevels.find((level) => level.value === cell))?.title)
            }
        },
        {
            dataField: "confluence_meta",
            text: t("Last Synced"),
            sort: false,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return cell?.synced_at ? dateUtils.convertDateToDate(cell.synced_at, INTERNATIONAL_DATE_TIME_FORMAT) : '---';
            }
        },
        {
            dataField: "confluence_meta",
            text: t("Last Updated"),
            sort: false,
            style: {
                width: '80px'
            },
            formatter: (cell) => {
                return cell?.content_updated_at ? dateUtils.convertDateToDate(cell.content_updated_at, INTERNATIONAL_DATE_TIME_FORMAT) : '---';
            }
        },
        {
            dataField: "created_by",
            text: t("Created By"),
            sort: false,
            style: {
                width: '140px'
            },
            formatter: (cell) => {
                return cell?.full_name || '---';
            }
        },
        {
            dataField: "actions",
            text: t("Actions"),
            sort: false,
            style: {
                width: '200px'
            },
            formatter: (_cell, row) => {
                const downloadPercent = downloadsPercents.find((percent) => percent.id === row.id);

                return (
                    <>
                        <>
                            <Button
                                id={`delete-attachment-${row.id}`}
                                color="link"
                                className="waves-effect me-1 text-danger"
                                onClick={() => setAttachmentToDelete(row)}
                            >
                                <i className="ri-delete-bin-line font-size-22"></i>
                            </Button>
                            <UncontrolledTooltip
                                placement="top"
                                target={`delete-attachment-${row.id}`}
                            >
                                {t('Delete Attachment')}
                            </UncontrolledTooltip>
                        </>

                        {row.content_type === 'file' ? (
                            <>
                                <Button
                                    id={`download-attachment-${row.id}`}
                                    color="link"
                                    className="waves-effect me-1"
                                    onClick={() => downloadFile(row)}
                                    disabled={downloadPercent}
                                >
                                    {downloadPercent ? (
                                        <>
                                            <Spinner
                                                size="sm"
                                                color="primary"
                                                className="me-1"
                                            />
                                            {downloadPercent.percent}%
                                        </>
                                    ) : (
                                        <i className="ri-download-line font-size-22"></i>
                                    )}
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`download-attachment-${row.id}`}
                                >
                                    {t('Download Attachment')}
                                </UncontrolledTooltip>
                            </>
                        ) : (
                            <>
                                <Button
                                    id={`document-link-${row.id}`}
                                    color="link"
                                    href={row.path}
                                    target="_blank"
                                    tag="a"
                                    rel="noopener noreferrer"
                                >
                                    <i className="ri-external-link-line font-size-22"></i>
                                </Button>
                                <UncontrolledTooltip
                                    placement="top"
                                    target={`document-link-${row.id}`}
                                >
                                    {t('View Document (opens in a new tab)')}
                                </UncontrolledTooltip>
                            </>
                        )}
                    </>
                )
            }
        }
    ];

    const NoDataIndication = () => attachmentsFetched && !attachmentsList?.length ? (
        <div className="alert m-0" role="alert">
            <p className="text-center mb-0">
                {t(EMPTY_LIST)}
            </p>
        </div>
    ) : (
        <></>
    );

    return (
        <>
            <AddAttachmentModal 
                isOpen={showAddAttachmentModal}
                toggle={() => setShowAddAttachmentModal(!showAddAttachmentModal)}
                onComplete={refetchAttachmentsList}
                softwareId={assetId}
            />
            <DeleteAttachmentModal
                isOpen={!!attachmentToDelete}
                toggle={() => setAttachmentToDelete(null)}
                onComplete={refetchAttachmentsList}
                attachment={attachmentToDelete}
            />

            <Accordion activeKey={activeAccordion} className="mt-4 attachments">
                <Accordion.Item
                    eventKey={ATTACHMENTS_ACCORDION_KEY}
                    data-information-accordion={ATTACHMENTS_ACCORDION_KEY}
                    className={activeAccordion === ATTACHMENTS_ACCORDION_KEY ? "active" : ""}
                >
                    <Accordion.Header onClick={handleAccordionClick}>
                        <img
                            src={PaperImage}
                            alt=""
                            className="me-2"
                        />
                        <span>{t("Attachments")}</span>
                    </Accordion.Header>

                    <Accordion.Body>
                        <PaginationProvider
                            pagination={paginationFactory({
                                sizePerPage: 25,
                                totalSize: attachmentsList?.length || 0,
                                custom: true,
                            })}
                            keyField="id"
                            columns={columns}
                            data={attachmentsList}
                        >
                            {({ paginationProps, paginationTableProps }) => (
                                <ToolkitProvider
                                    keyField="id"
                                    columns={columns}
                                    data={attachmentsList}
                                    search
                                >
                                    {(toolkitProps) => (
                                        <>
                                            <Row className="mb-3">
                                                <Col sm="12">
                                                    <div className="d-flex gap-2 justify-content-end">
                                                        <Button
                                                            color="primary"
                                                            style={{
                                                                display: "block",
                                                                height: "max-content"
                                                            }}
                                                            onClick={() => setShowAddAttachmentModal(true)}
                                                            outline
                                                        >
                                                            {t("Add Attachment")}
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col xl="12">
                                                    <div className="table-responsive">
                                                        <BootstrapTable
                                                            keyField={"id"}
                                                            responsive
                                                            bordered={false}
                                                            striped={false}
                                                            defaultSorted={[]}
                                                            loading={attachmentsAreFetching}
                                                            overlay={overlayFactory({
                                                                spinner: (
                                                                    <Spinner 
                                                                        animation="border" 
                                                                        variant="primary" 
                                                                        size="md" 
                                                                    />
                                                                ),
                                                                text: `${t('Loading')}...`,
                                                            })}
                                                            noDataIndication={() => <NoDataIndication />}
                                                            classes="table align-middle table-nowrap"
                                                            headerWrapperClasses="thead-light"
                                                            {...toolkitProps.baseProps}
                                                            {...paginationTableProps}
                                                        />
                                                    </div>
                                                </Col>
                                            </Row>

                                            <Row className="align-items-md-center mt-30">
                                                <Col className="inner-custom-pagination d-flex">
                                                    <div className="d-inline">
                                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                                    </div>
                                                    <div className="text-md-right ms-auto">
                                                        <PaginationListStandalone {...paginationProps} />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </>
                                    )}
                                </ToolkitProvider>
                            )}
                        </PaginationProvider>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        </>
    )
}

const mapStatetoProps = (state) => {
	const { token } = state.Login;

	return {
		token
	};
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        Attachments
    )
);
